<template>
  <div class="w-full">
    <h2 class="mb-8">Your Connections</h2>
    <FilterFormKeywords
      :is-nested="false"
      class="mb-8"
      @on-submit="filterConnections"
    />
    <div
      v-show="!isContributor"
      class="flex bg-truegray-100 w-[max-content] rounded-lg overflow-hidden mb-6"
    >
      <button
        v-for="(tab, i) in CONNECTIONS_TABS"
        :key="i"
        :title="tab.title"
        class="py-3 text-sm px-10 flex justify-center items-center transition-all duration-100 ease-in-out"
        :class="[
          isActiveConnectionsTab(i) && tab.context === 'CW'
            ? 'border border-cw-alt text-cw-alt bg-white rounded-lg'
            : isActiveConnectionsTab(i)
              ? 'border border-primary text-primary bg-white rounded-lg'
              : 'bg-truegray-100',
        ]"
        @click="() => changeConnectionsTab(i)"
      >
        {{ tab.name }}
      </button>
    </div>

    <div v-if="!loading" class="flex flex-col space-y-4">
      <div
        v-for="connection in connections"
        :key="connection.id"
        class="flex items-center justify-between cursor-pointer rounded-l-full rounded-r-xl hover:bg-truegray-100 pr-4"
        :class="isSelected(connection.id) ? 'bg-truegray-100' : ''"
        @click="toggleAttachConnection(connection)"
      >
        <!-- Connection info -->
        <ListConnection :connection="connection" />

        <!-- Select -->
        <div
          class="w-5 h-5 border-2 rounded-full flex items-center justify-center transition-background transition-border duration-300 ease-in-out"
          :class="
            isSelected(connection.id)
              ? isContributor
                ? 'bg-cw-alt border-cw-alt'
                : 'bg-primary border-primary'
              : 'border-gray-700'
          "
        >
          <svg
            :class="isSelected(connection.id) ? 'opacity-100' : 'opacity-0'"
            :style="[{ left: '1px' }, { top: '1px' }]"
            xmlns="http://www.w3.org/2000/svg"
            class="relative h-5 w-5 text-white cursor-pointer transition-all duration-100"
            fill="currentColor"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
      </div>
      <p v-if="!connections?.length" class="italic">{{ noConnectionsText }}</p>
    </div>
    <div v-else class="w-full h-16 flex items-center justify-center">
      <lottie :options="loadingAnimationOptions" />
    </div>

    <!-- Pagination -->
    <div
      v-if="
        connectionsPagination.previouspage || connectionsPagination.nextpage
      "
      class="flex justify-between items-center h-10"
    >
      <span
        :disabled="!connectionsPagination.previouspage"
        :class="[
          !connectionsPagination.previouspage
            ? 'opacity-25 cursor-not-allowed'
            : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
        ]"
        class="text-gray-500"
        @click="getConnections({ page: connectionsPagination.previouspage })"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M11 17l-5-5m0 0l5-5m-5 5h12"
          />
        </svg>
      </span>
      <span class="text-center text-xs"
        >Page {{ connectionsPagination.current_page }} of
        {{ connectionsPagination.last_page }}</span
      >
      <span
        :disabled="!connectionsPagination.nextpage"
        :class="[
          !connectionsPagination.nextpage
            ? 'opacity-25 cursor-not-allowed'
            : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
        ]"
        class="text-gray-500"
        @click="getConnections({ page: connectionsPagination.nextpage })"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 7l5 5m0 0l-5 5m5-5H6"
          />
        </svg>
      </span>
    </div>
    <div v-if="connections && connections.length" class="pt-8 mb-0">
      <button class="btn btn-black w-32 mx-auto mb-0" @click="onSubmit">
        Save
      </button>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useStore } from '../../../../../js/store/index.js'
import * as loadingAnimation from '../../../../../shared/components/loading-dark.json'
import { EventBus } from '../../../../../js/plugins/event-bus'
import FilterFormKeywords from '../FilterFormKeywords.vue'
import ListConnection from '../ListConnection.vue'

export default defineComponent({
  name: 'ModalAttachConnections',
  components: { FilterFormKeywords, ListConnection },

  props: {
    config: { type: Object, required: true },
  },

  setup(props) {
    const store = useStore()
    const loadingAnimationOptions = { animationData: loadingAnimation.default }
    const loading = ref(false)
    const animating = ref(false)

    const CONTEXT_MAP = {
      CONTRIBUTOR: {
        UPDATE_CONNECTIONS: 'contributors/dispatchUpdateConnections',
        GET_CONNECTIONS: 'contributors/getConnections',
      },

      GETTERS: {
        SELECTED_CONNECTIONS: {
          REPORTS: 'connections/getReportSelectedConnections',
          POSTS: 'connections/getPostSelectedConnections',
        },
      },

      ACTIONS: {
        UPDATE_SELECTED_CONNECTIONS: {
          REPORTS: 'connections/dispatchUpdateSelectedReportConnections',
          POSTS: 'connections/dispatchUpdateSelectedPostConnections',
        },
      },

      DATA: {
        TEXT_NO_CONNECTIONS: {
          REPORTS: 'No connections found...',
          POSTS: 'No public connections found...',
        },
      },
    }

    const isContributor = computed(() => store.getters['shared/isContributor'])
    const parentContext = computed(() => props.config.context)
    const connections = computed(() => {
      if (isContributor.value) {
        return store.getters[CONTEXT_MAP.CONTRIBUTOR.GET_CONNECTIONS]
      }

      return store.getters[activeConnectionsTab.value.getter]
    })

    const selectedConnections = computed(() => {
      const getter =
        CONTEXT_MAP.GETTERS.SELECTED_CONNECTIONS[parentContext.value]
      return store.getters[getter]
    })

    const updatedConnections = ref([...selectedConnections.value])

    const reportId = computed(() => props.config?.reportId || null)
    const postId = computed(() => props.config?.postId || null)

    const noConnectionsText = computed(() => {
      const key =
        isContributor.value || activeConnectionsTab.value.context === 'CW'
          ? 'REPORTS'
          : parentContext.value

      return CONTEXT_MAP.DATA.TEXT_NO_CONNECTIONS[key]
    })

    const getConnections = async ({
      context = activeConnectionsTab.value.context,
      page = 1,
      filters = null,
      perPage = 8,
    } = {}) => {
      loading.value = true
      const action = isContributor.value
        ? CONTEXT_MAP.CONTRIBUTOR.UPDATE_CONNECTIONS
        : activeConnectionsTab.value.action
      const paginationObj = await store.dispatch(action, {
        context,
        page,
        filters,
        perPage,
        ...(reportId.value ? { reportId: reportId.value } : {}),
        ...(postId.value ? { postId: postId.value } : {}),
      })

      animating.value = false
      loading.value = false
      if (paginationObj) {
        setConnectionsPagination(paginationObj)
      }
    }

    const filterConnections = ({ keywords }) => {
      getConnections({
        page: connectionsPagination.value.current_page,
        filters: { keywords },
      })
    }

    const connectionsPagination = ref({
      current_page: null,
      per_page: 8,
      next_page: null,
      previous_page: null,
      last_page_url: null,
      next_page_url: null,
      prev_page_url: null,
      path: null,
      nextpage: null,
      previouspage: null,
      total: 0,
    })

    const setConnectionsPagination = (paginationObj) => {
      let prev = null
      if (paginationObj.prev_page_url) {
        prev = paginationObj.prev_page_url.split('=')
        prev = prev[1]
      }

      let next = null
      if (paginationObj.next_page_url) {
        next = paginationObj.next_page_url.split('=')
        next = next[1]
      }

      paginationObj.previouspage = prev
      paginationObj.nextpage = next

      connectionsPagination.value = paginationObj
    }

    const toggleAttachConnection = (connection) => {
      const index = updatedConnections.value.findIndex(
        (c) => c.id === connection.id,
      )

      if (index > -1) {
        updatedConnections.value.splice(index, 1)
      } else {
        updatedConnections.value.push(connection)
      }
    }

    const isSelected = (id) => {
      return !!updatedConnections.value.find(
        (connection) => connection.id === id,
      )
    }

    const onSubmit = () => {
      // Dispatch the action with the updated array
      const action =
        CONTEXT_MAP.ACTIONS.UPDATE_SELECTED_CONNECTIONS[parentContext.value]
      store.dispatch(action, updatedConnections.value)

      EventBus.$emit('showModal', false)
    }

    // TABS
    const CONNECTIONS_TABS = computed(() => {
      const tabs = [
        {
          name: 'My Connections',
          context: 'SAVED',
          action: 'connections/dispatchUpdateConnections',
          getter: 'connections/getConnections',
        },

        {
          name: 'CareerWise',
          context: 'CW',
          action: 'connections/dispatchUpdateOrganisationConnections',
          getter: 'connections/getOrganisationConnections',
        },
      ]

      if (isContributor.value) {
        return tabs.slice(1)
      }

      return tabs
    })

    const isActiveConnectionsTab = (i) => {
      return activeConnectionsTab.value === CONNECTIONS_TABS.value[i]
    }

    const setActiveConnectionsTab = (i = 0) => {
      activeConnectionsTab.value = CONNECTIONS_TABS.value[i]
    }

    const changeConnectionsTab = (i) => {
      animating.value = true
      setTimeout(() => {
        setActiveConnectionsTab(i)
        const context = CONNECTIONS_TABS.value[i].context
        getConnections({ context })
      }, 100)
    }

    const activeConnectionsTab = ref(CONNECTIONS_TABS.value[0])

    onMounted(() => {
      getConnections()
    })

    return {
      loadingAnimationOptions,
      loading,
      animating,
      isContributor,
      connections,
      noConnectionsText,
      connectionsPagination,
      selectedConnections,
      isSelected,
      toggleAttachConnection,
      getConnections,
      filterConnections,
      onSubmit,

      // TABS
      CONNECTIONS_TABS,
      isActiveConnectionsTab,
      setActiveConnectionsTab,
      changeConnectionsTab,
    }
  },
})
</script>
